import React, { useMemo } from "react";

/* Derive ring props from the site `settings` object. */
export function ringConfigFromSettings(s = {}) {
  return {
    enabled: s.header_logo_ring_enabled !== false,
    variant: s.header_logo_ring_variant || "rainbow",
    thickness: Number(s.header_logo_ring_thickness ?? 3),
    gap: Number(s.header_logo_ring_gap ?? 2),
    animation: s.header_logo_ring_animation || "glow",
    speed: Number(s.header_logo_ring_speed ?? 6),
    direction: s.header_logo_ring_direction === "reverse" ? "reverse" : "normal",
    intensity: Number(s.header_logo_ring_intensity ?? 25),
    color: s.header_logo_ring_color || "#000000",
    color2: s.header_logo_ring_color2 || "",
    glowColor: s.header_logo_ring_glow_color || "#ff00c8",
    size: Number(s.header_logo_size ?? 56),
  };
}

/* Animated, fully configurable ring around a circular logo. */
export default function HeaderLogoRing({
  src,
  enabled = true,
  variant = "rainbow",
  thickness = 3,
  gap = 2,
  animation = "glow",
  speed = 6,
  direction = "normal",
  intensity = 25,
  color = "#000000",
  color2 = "",
  glowColor = "#ff00c8",
  size = 56,
}) {
  const flakes = useMemo(
    () => Array.from({ length: 8 }).map((_, i) => ({ angle: (i * 360) / 8, delay: (i * 0.4).toFixed(2) })),
    []
  );

  const dir = direction === "reverse" ? "reverse" : "normal";
  const spinAnim = `ring-spin ${Math.max(1, speed)}s linear infinite ${dir}`;
  const showRing = enabled && variant !== "none";
  const dim = `${size}px`;
  const glowOn = animation === "glow";

  // Donut mask leaving a ring of `thickness` at the outer edge.
  const donutMask = `radial-gradient(closest-side, transparent calc(100% - ${thickness}px), #000 calc(100% - ${thickness}px))`;
  const isPaint = variant === "rainbow" || variant === "gradient";
  const paintBg =
    variant === "rainbow"
      ? "conic-gradient(from 0deg, #FF6B6B, #FFD93D, #005F73, #2D6A4F, #FF6B6B)"
      : color2
      ? `conic-gradient(from 0deg, ${color}, ${color2}, ${color})`
      : color;

  return (
    <span className="relative inline-flex items-center justify-center rounded-full bg-white"
      style={{ width: dim, height: dim, padding: `${thickness + gap}px` }} data-testid="header-logo-ring">
      {/* Glow halo */}
      {glowOn && (
        <span aria-hidden className="absolute -inset-1 rounded-full blur-md"
          style={{
            background: `radial-gradient(circle, ${glowColor} 0%, transparent 70%)`,
            opacity: Math.max(0, Math.min(1, intensity / 100)),
            animation: `ring-glow-pulse ${Math.max(1, speed) / 2}s ease-in-out infinite`,
          }} />
      )}

      {/* Ring */}
      {showRing && isPaint && (
        <span aria-hidden className="absolute rounded-full"
          style={{
            inset: `${-gap}px`,
            background: paintBg,
            WebkitMask: donutMask,
            mask: donutMask,
            animation: animation === "spin" ? spinAnim : undefined,
          }} />
      )}
      {showRing && !isPaint && (
        <>
          <span aria-hidden className="absolute rounded-full"
            style={{
              inset: `${-gap}px`,
              border: `${thickness}px ${variant === "dotted" ? "dotted" : "solid"} ${color}`,
              animation: variant === "dotted" && animation === "spin" ? spinAnim : undefined,
            }} />
          {animation === "pulse" && (
            <span aria-hidden className="absolute rounded-full"
              style={{ inset: `${-gap}px`, border: `${thickness}px solid ${color}`, animation: `ring-pulse ${Math.max(1, speed) / 3}s ease-out infinite` }} />
          )}
        </>
      )}

      {/* Snow orbit decoration */}
      {showRing && variant === "snow" &&
        flakes.map((f, i) => (
          <span key={i} aria-hidden className="absolute left-1/2 top-1/2"
            style={{
              width: 4, height: 4, borderRadius: "9999px", background: "#FFFFFF",
              boxShadow: "0 0 4px rgba(255,255,255,0.9)",
              transform: `rotate(${f.angle}deg) translateY(-130%)`,
              transformOrigin: "0 0",
              animation: `snow-orbit 3s linear ${f.delay}s infinite`,
            }} />
        ))}

      <img src={src} alt="" className="relative w-full h-full object-cover rounded-full" />

      <style>{`
        @keyframes ring-spin { to { transform: rotate(360deg); } }
        @keyframes ring-pulse { 0% { transform: scale(0.92); opacity: 0.8; } 100% { transform: scale(1.45); opacity: 0; } }
        @keyframes ring-glow-pulse { 0%,100% { opacity: ${Math.max(0, Math.min(1, intensity / 100)) * 0.5}; } 50% { opacity: ${Math.max(0, Math.min(1, intensity / 100))}; } }
        @keyframes snow-orbit { 0% { opacity: 0; } 30% { opacity: 1; } 100% { opacity: 0; transform: rotate(var(--a)) translateY(-200%); } }
      `}</style>
    </span>
  );
}
